import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import classNames from 'classnames';
import { Formik } from 'formik';
import Skeleton from 'react-loading-skeleton';
import * as Yup from 'yup';

import { formatFigure, formatMoney } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import {
    RISK_NODE_ITEMS_CHANGED,
    RiskNodeItemFields,
    riskNodeItemInitialValues,
    riskNodeItemPayload,
    riskNodeItemSchema,
} from 'CommonComponents/modals/RiskNodeItemFields';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextButton from 'CommonComponents/TextButton';
import TextFormField from 'CommonComponents/TextFormField';
import ToggleFormField from 'CommonComponents/ToggleFormField';

import { ConfirmationModalProvider, useConfirmationModalContext } from 'Context/ConfirmationModalContext';

import useApiRequest from 'Hooks/useApiRequest';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

// Mirror the quick-create modal's field set / validation so the in-place editor
// behaves identically (IB-729).
const EntrySchema = Yup.object().shape({
    name: Yup.string().required($t('This value is required')),
    description: Yup.string().nullable(),
    AFC: Yup.bool(),
    amount: Yup.number().nullable().min(0),
});

const readNode = (el) => ({
    kind: el.getAttribute('data-node-kind'),
    id: el.getAttribute('data-node-id'),
    name: el.getAttribute('data-node-name'),
});

const nodeUrl = (template, node, entryId = null) => {
    const url = template.replace('NODE_KIND', node.kind).replace('NODE_ID', node.id);

    return entryId === null ? url : url.replace('ENTRY_ID', entryId);
};

/**
 * The risks or opportunities behind a budget grid row's figure: the items at
 * the row's node and below, each with what it is attached to.
 */
const ExpenseRiskOpportunityListModal = ({ urls, createModalSelector, impacts, probabilities }) => {
    const rootEl = document.getElementById('ExpenseRiskOpportunityListModalApp');

    const [node, setNode] = useState(() => readNode(rootEl));
    const [type, setType] = useState(rootEl.getAttribute('data-type'));

    const [entries, setEntries] = useState([]);
    const [canCreate, setCanCreate] = useState(false);
    const [editingId, setEditingId] = useState(null);
    // Incremented each time the modal is opened so that re-clicking the same
    // row+column always re-fetches fresh data, even when the node and type
    // have not changed (Verified Decision #1 — re-fetch on every open).
    const [openCounter, setOpenCounter] = useState(0);

    const toast = useToast();
    const confirm = useConfirmationModalContext();

    const modal = useModal({
        options: {
            ...modalOptions(true),
            backdrop: 'dynamic',
        },
    });

    const isRisk = type === 'risk';

    const listUrl = useMemo(() => (isRisk ? urls.fetchRisks : urls.fetchOpportunities), [isRisk, urls]);
    const updateUrlTemplate = useMemo(() => (isRisk ? urls.updateRisk : urls.updateOpportunity), [isRisk, urls]);
    const deleteUrlTemplate = useMemo(() => (isRisk ? urls.deleteRisk : urls.deleteOpportunity), [isRisk, urls]);

    const listRequest = useApiRequest({ url: listUrl });

    // ── Mount: watch the host div's data-* attributes ───────────────────────
    useEffect(() => {
        const targetNode = document.getElementById('ExpenseRiskOpportunityListModalApp');

        const callback = function (mutationsList) {
            for (const mutation of mutationsList) {
                if (mutation.type !== 'attributes') {
                    continue;
                }

                if (mutation.attributeName === 'data-type') {
                    // One render, so the list is fetched once per open.
                    ReactDOM.unstable_batchedUpdates(() => {
                        setNode(readNode(mutation.target));
                        setType(mutation.target.getAttribute('data-type'));
                        setEditingId(null);
                        setOpenCounter((c) => c + 1);
                    });
                    modal.show();
                }
            }
        };

        const observer = new MutationObserver(callback);
        observer.observe(targetNode, { attributes: true });

        return () => observer.disconnect();
    }, []);

    // ── Re-fetch when the targeted node / type changes ───────────────────────
    const fetchEntries = useCallback(() => {
        if (!node.kind || !node.id || !type) {
            return;
        }

        listRequest.request({ method: 'get', url: nodeUrl(listUrl, node) });
    }, [node, type, listUrl, listRequest]);

    useEffect(() => {
        fetchEntries();
    }, [node, type, openCounter]);

    useEffect(() => {
        setEntries(listRequest.data?.data || []);
        setCanCreate(!!listRequest.data?.can_create);
    }, [listRequest.data]);

    // Add-while-open: when an entry is created from the create modal, re-fetch.
    // The grid itself refreshes when the reporting job broadcasts.
    useEffect(() => {
        const handler = () => fetchEntries();
        window.addEventListener(RISK_NODE_ITEMS_CHANGED, handler);
        return () => window.removeEventListener(RISK_NODE_ITEMS_CHANGED, handler);
    }, [fetchEntries]);

    // Only in-AFC items count towards the row figure, so this total equals it.
    const total = entries.reduce((sum, entry) => sum + (entry.afc ? parseFloat(entry.amount) || 0 : 0), 0);

    const modalTitle = useMemo(() => (isRisk ? $t('Risks') : $t('Opportunities')), [isRisk]);

    const nodeLabel = listRequest.data?.node?.label || node.name;

    const editSchema = useMemo(() => riskNodeItemSchema(type, EntrySchema), [type]);

    // ── Save (PATCH) an edited entry ─────────────────────────────────────────
    const saveEntry = useCallback(
        (entryId, payload) => axios.patch(nodeUrl(updateUrlTemplate, node, entryId), riskNodeItemPayload(type, payload)),
        [updateUrlTemplate, node, type],
    );

    // ── Delete an entry behind a confirmation ────────────────────────────────
    const deleteEntry = useCallback(
        (entry) => {
            const message = isRisk
                ? $t('Are you sure you want to delete "{{name}}"? This will also remove the entry from the RADIO register.', { name: entry.name })
                : $t('Are you sure you want to delete "{{name}}"?', { name: entry.name });

            const warning =
                isRisk && entry.has_radio_detail
                    ? $t('This risk has detailed RADIO information recorded against it, which will be permanently removed.')
                    : undefined;

            confirm({
                title: isRisk ? $t('Delete risk') : $t('Delete opportunity'),
                message,
                warning,
                onConfirm: () => {
                    return axios
                        .delete(nodeUrl(deleteUrlTemplate, node, entry.id))
                        .then(() => {
                            toast.success(isRisk ? $t('The risk has been deleted.') : $t('The opportunity has been deleted.'));
                            fetchEntries();
                        })
                        .catch(() => {
                            toast.error($t('An error occurred, please try again'));
                        });
                },
            });
        },
        [isRisk, confirm, deleteUrlTemplate, node, toast, fetchEntries],
    );

    // ── Open the create modal, pre-scoped to this node ───────────────────────
    const openCreateModal = useCallback(() => {
        const createEl = document.querySelector(createModalSelector);
        if (!createEl) {
            return;
        }
        // Both modals share the same z-index and flowbite does not stack
        // backdrops, so opening the create modal on top of the list modal would
        // render it behind. Hide the list first, then open the create modal once
        // the list's backdrop has been torn down.
        modal.hide();
        setTimeout(() => {
            createEl.setAttribute('data-node-kind', node.kind);
            createEl.setAttribute('data-node-id', node.id);
            createEl.setAttribute('data-node-name', nodeLabel);
            createEl.setAttribute('data-type', type);
        }, 200);
    }, [createModalSelector, node, nodeLabel, type, modal]);

    const renderViewRow = (entry) => (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-py-1.5">
            <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-truncate tw-text-charcoal tw-font-medium">{entry.name}</div>
                {entry.attached_to && (
                    <div className="tw-truncate tw-text-2xs tw-text-charcoal-75">{$t('Attached to {{name}}', { name: entry.attached_to.name })}</div>
                )}
                {entry.afc && (
                    <span className="tw-inline-block tw-text-2xs tw-font-medium tw-text-green-600 tw-bg-green-100 tw-rounded-md tw-px-2 tw-py-0.5">
                        {$t('In AFC')}
                    </span>
                )}
            </div>
            <div className="tw-flex tw-items-center tw-gap-x-2 tw-flex-shrink-0">
                <span className="inbud-numeric-cell tw-text-charcoal tw-tabular-nums">{formatFigure(entry.amount, formatMoney, { precision: 3 })}</span>
                {entry.can_update && (
                    <TextButton variant="primary" onClick={() => setEditingId(entry.id)} title={$t('Edit')}>
                        <EditIcon className="tw-w-3 tw-h-3" />
                    </TextButton>
                )}
                {entry.can_delete && (
                    <TextButton variant="danger" onClick={() => deleteEntry(entry)} title={$t('Delete')}>
                        <TrashIcon className="tw-w-3 tw-h-3" />
                    </TextButton>
                )}
            </div>
        </div>
    );

    const renderEditRow = (entry) => (
        <div className="tw-p-3.5 tw-bg-slate-50 tw-rounded">
            <Formik
                enableReinitialize
                initialValues={{
                    name: entry.name || '',
                    description: entry.description || '',
                    amount: entry.amount || 0,
                    AFC: !!entry.afc,
                    ...riskNodeItemInitialValues(type, entry),
                }}
                validationSchema={editSchema}
                onSubmit={(values, actions) => {
                    saveEntry(entry.id, values)
                        .then(() => {
                            toast.success(isRisk ? $t('The risk has been updated.') : $t('The opportunity has been updated.'));
                            setEditingId(null);
                            fetchEntries();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => actions.setSubmitting(false));
                }}
            >
                {({ values, isSubmitting, handleSubmit, setFieldValue }) => (
                    <div className="tw-space-y-2.5">
                        <div className="tw-text-2xs tw-font-medium tw-text-charcoal-50 tw-uppercase">{$t('Editing')}</div>
                        <div>
                            <TextFormField
                                label={$t('Name')}
                                type="text"
                                name="name"
                                placeholder={isRisk ? $t('Enter risk name') : $t('Enter opportunity name')}
                            />
                        </div>
                        <div>
                            <TextareaFormField
                                label={$t('Description')}
                                as="textarea"
                                type="text"
                                name="description"
                                placeholder={$t('Allowance for unplanned additional service volume')}
                            />
                        </div>
                        <RiskNodeItemFields type={type} impacts={impacts} probabilities={probabilities} ratingsEditable={Boolean(entry.has_radio_record)} />
                        <div className="tw-flex tw-items-center tw-gap-x-3">
                            <div className="tw-flex-1">
                                <TextFormField label={$t('Amount')} type="number" name="amount" />
                            </div>
                            <div className="tw-pt-[21px]">
                                <ToggleFormField
                                    label={$t('In AFC')}
                                    type="checkbox"
                                    name="AFC"
                                    id={`AFC-${entry.id}`}
                                    checked={values.AFC}
                                    onChange={() => setFieldValue('AFC', !values.AFC)}
                                />
                            </div>
                        </div>
                        <ModalFooter
                            cancelButtonText={$t('Cancel')}
                            primaryButtonText={$t('Save')}
                            onCancel={() => setEditingId(null)}
                            handleSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            submitButtonVariant={'primary'}
                        />
                    </div>
                )}
            </Formik>
        </div>
    );

    return (
        <Modal id="expense-risk-opportunity-list-modal" modal={modal} parentContainerClasses="tw-max-w-lg">
            <ModalHeader subtitle={nodeLabel} close={() => modal.hide()}>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <span className={classNames('tw-inline-block tw-w-2.5 tw-h-2.5 tw-rounded-full', isRisk ? 'tw-bg-red' : 'tw-bg-green')} />
                    <h3 className="tw-text-2xl tw-font-semibold tw-text-blue">{modalTitle}</h3>
                </div>
            </ModalHeader>
            <div className="modal__form-container">
                <div className="tw-max-h-[374px] tw-overflow-y-auto tw-divide-y-2 tw-divide-slate-50">
                    {listRequest.loading ? (
                        <div className="tw-py-2 tw-space-y-2">
                            <Skeleton width="100%" height={28} />
                            <Skeleton width="100%" height={28} />
                            <Skeleton width="100%" height={28} />
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="tw-text-center tw-py-6 tw-text-charcoal-75">{isRisk ? $t('No risks found') : $t('No opportunities found')}</div>
                    ) : (
                        entries.map((entry) => (
                            <div key={entry.id} className="tw-py-1">
                                {editingId === entry.id ? renderEditRow(entry) : renderViewRow(entry)}
                            </div>
                        ))
                    )}
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-3 tw-pt-3">
                    {canCreate ? (
                        <TextButton variant="primary" onClick={openCreateModal}>
                            <CirclePlusIcon className="tw-w-3 tw-h-3" /> {isRisk ? $t('Add risk') : $t('Add opportunity')}
                        </TextButton>
                    ) : (
                        <span />
                    )}
                    <div className="tw-flex tw-items-baseline tw-gap-x-2">
                        <span className="tw-text-sm tw-text-charcoal-75">{$t('Total in AFC')}</span>
                        <span className="tw-font-semibold">{formatFigure(total, formatMoney, { precision: 3 })}</span>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

if (document.getElementById('ExpenseRiskOpportunityListModalApp')) {
    var rootEl = document.getElementById('ExpenseRiskOpportunityListModalApp');

    var urls = {
        fetchRisks: rootEl.getAttribute('data-fetch-risks-url'),
        fetchOpportunities: rootEl.getAttribute('data-fetch-opportunities-url'),
        updateRisk: rootEl.getAttribute('data-update-risk-url'),
        updateOpportunity: rootEl.getAttribute('data-update-opportunity-url'),
        deleteRisk: rootEl.getAttribute('data-delete-risk-url'),
        deleteOpportunity: rootEl.getAttribute('data-delete-opportunity-url'),
    };

    var createModalSelector = rootEl.getAttribute('data-create-modal-selector');
    var parseList = function (name) {
        try {
            return JSON.parse(rootEl.getAttribute(name) || '[]');
        } catch (e) {
            return [];
        }
    };

    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <ExpenseRiskOpportunityListModal
                    urls={urls}
                    createModalSelector={createModalSelector}
                    impacts={parseList('data-impacts')}
                    probabilities={parseList('data-probabilities')}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
